import {
	actionMailItems,
	addItemToCustomFolder,
	createCustomFolder,
	createForwardingBatch,
	deleteCustomFolder,
	editCustomFolder,
	FilterableMailBoxUrlSlugs,
	InfiniteData,
	MailBoxFolderNameMap,
	MailBoxSlugs,
	MailBoxUrlSlugs,
	MailFolderUpdateType,
	MailItemUpdateType,
	PrivateMailBoxUrlSlugs,
	removeItemFromCustomFolder,
	updateMailNote,
	updateMarkMailItemAsRead,
	useCustomMailFolders,
	useInfiniteMailFolders,
	useMailItem,
	useUpdateCustomFolders,
	useUpdateMailBox,
} from '@ukpostbox/client-data';
import { CustomMailFolderType } from '@ukpostbox/client-data/lib/Types/FolderTypes';
import { CreateForwardingBatchType } from '@ukpostbox/client-data/lib/Types/ForwardingBatchTypes';
import { MailItemType } from '@ukpostbox/client-data/lib/Types/MailItemTypes';
import { useComboBoxSimple } from '@ukpostbox/frontend-framework';
import { GetArrayIndexByKeyValue } from '@ukpostbox/helpers';
import React, {
	PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { MailBoxResponseType } from '@ukpostbox/client-data/lib/Types/Response/MailBoxResponseTypes';
import { DropCustomFolder } from '../Components/SharedFunctions/CustomFolders';
import ConfirmActionModal from '../Components/Modals/ConfirmActionModal';
import CreateCustomFolderModal from '../Components/Modals/CreateCustomFolderModal';
import { DeleteCustomFolderModal } from '../Components/Modals/DeleteCustomFolderModal';
import { FilterMailboxModal } from '../Components/Modals/FilterMailboxModal';
import { ForwardItemsModal } from '../Components/Modals/ForwardItemsModal';
import { MailboxFilterUrlMapping, MailboxUrlMapping, MapFolderSlugToId } from '../Utils/MailUrlMapping';
import { DeleteFilter, UpdateAllFilters } from '../../_Global/Utils/UrlSlugsAndFilters';
import useMailItemActions from '../../MailItem/Hooks/useMailItemActions';

import { MailboxContextType, MailboxInfoType, MailboxProviderType } from '../Types/MailboxProviderTypes';
import { useMailboxesStore } from './Stores/MailboxesStore';
import { MailboxUrlParamsType } from '../Types/MailboxUtilsTypes';

const MailBoxGridSize = ['', 'grid-item-md', 'grid-item-lg'];

const MailboxContext = React.createContext<MailboxContextType>({} as MailboxContextType);

function MailboxProvider(props: PropsWithChildren<MailboxProviderType>) {
	const params = useParams();
	const location = useLocation();
	const navigate = useNavigate();

	const updateMailBox = useUpdateMailBox();
	const updateCustomFolder = useUpdateCustomFolders();

	const dispatchMailboxesStoreUpdate = useMailboxesStore((state) => state.dispatchMailboxesStoreUpdate);

	const parentFolderId = useMailboxesStore((state) => state.parentFolderId);
	const itemMoving = useMailboxesStore((state) => state.itemMoving);
	const showFilterModal = useMailboxesStore((state) => state.showFilterModal);
	const createCustomFolderOpen = useMailboxesStore((state) => state.createCustomFolderOpen);
	const deleteCustomFolderOpen = useMailboxesStore((state) => state.deleteCustomFolderOpen);
	const activeCustomFolderData = useMailboxesStore((state) => state.activeCustomFolderData);
	const confirmActionData = useMailboxesStore((state) => state.confirmActionData);
	const forwardItemsData = useMailboxesStore((state) => state.forwardItemsData);
	const mailBoxInfo = useMailboxesStore((state) => state.mailBoxInfo);
	const mailItemRequiresTopUp = useMailboxesStore((state) => state.mailItemRequiresTopUp);
	const mailItemOnHold = useMailboxesStore((state) => state.mailItemOnHold);
	const mailFolderModificationActive = useMailboxesStore((state) => state.mailFolderModificationActive);
	const mailNote = useMailboxesStore((state) => state.mailNote);
	const preItemUpdate = useMailboxesStore((state) => state.preItemUpdate);

	const [urlSearchParams, setUrlSearchParams] = useSearchParams();
	const [urlParams, setUrlParams] = useState<MailboxUrlParamsType>(params);
	const [searchParams, setSearchParams] = useState<URLSearchParams>(urlSearchParams);

	const [searchActive, setSearchActive] = useState<boolean>(location.pathname.indexOf('/mail/search') > -1);
	const [searchReset, setSearchReset] = useState<boolean>(false);
	const [lastNonSearchLocation, setLastNonSearchLocation] = useState<string>('');

	const initialFolder = urlParams.folderSlug
		? urlParams.folderSlug
		: urlParams.clientFolderIds
			? MailBoxSlugs[-1]
			: urlParams.mailTagIds
				? MailBoxSlugs[-3]
				: urlParams.unreadStatus
					? MailBoxSlugs[-2]
					: 'inbox';

	const { status: folderStatus, data: folderData, error: folderError } = useInfiniteMailFolders();
	const { status: customFolderStatus, data: customFolderData, error: customFolderError } = useCustomMailFolders();
	const { status: mailItemStatus, data: mailItemData, error: mailItemError } = useMailItem(params.mailId as string);

	const {
		allSelected,
		visibleSelected,
		visibleIndeterminate,
		selectedItems,
		ToggleAll,
		ToggleSelected,
		ToggleVisible,
		ResetSelection,
	} = useComboBoxSimple();

	const selectedItemsRef = useRef<Array<any>>(selectedItems);

	const [gridView, setGridView] = useState<boolean>(
		localStorage.getItem(`${process.env.REACT_APP_SITE_KEY}-mailbox-view-type`) === 'grid',
	);
	const [gridSize, setGridSize] = useState<any>(
		localStorage.getItem(`${process.env.REACT_APP_SITE_KEY}-mailbox-grid-size`)
			? JSON.parse(localStorage.getItem(`${process.env.REACT_APP_SITE_KEY}-mailbox-grid-size`) as string)
			: { size: 0, className: MailBoxGridSize[0] },
	);

	const [userFilters, setUserFilters] = useState<FilterableMailBoxUrlSlugs>(
		MailboxFilterUrlMapping(urlParams, location),
	);
	const [privateFilters, setPrivateFilters] = useState<PrivateMailBoxUrlSlugs>(
		MailboxUrlMapping(urlParams, location),
	);
	const [parsedFilters, setParsedFilters] = useState<MailBoxUrlSlugs>({ ...privateFilters, ...userFilters });

	const { selectionActions, mailItemActionsError, mailItemActionsStatus, FilterActions } = useMailItemActions();

	const UpdateMailboxInfo = () => {
		const customFolder = customFolderData
			? customFolderData[
					GetArrayIndexByKeyValue(customFolderData, 'id', parseInt(urlParams.clientFolderIds as string, 10))
				]
			: null;
		const folderParentFolderId = customFolder && customFolder.parentFolderId ? customFolder.parentFolderId : -1;

		if (urlParams.clientFolderIds && !customFolder) {
			if (parentFolderId !== -1) {
				navigate(`/mail/custom-folder/${parentFolderId}`);
			} else {
				navigate('/mail/inbox');
			}
		} else {
			const newData: MailboxInfoType = {
				folderId: MapFolderSlugToId(urlParams),
				folderName:
					(MailBoxFolderNameMap as any)[urlParams.folderSlug as any] ||
					(customFolder && customFolder.description) ||
					(userFilters && 'Search'),
				folderSlug: urlParams.clientFolderIds
					? 'custom-folder'
					: urlParams.mailTagIds
						? 'tagged'
						: urlParams.folderSlug || initialFolder,
				folderColour: customFolder ? customFolder.colour : undefined,
				mailTagIds: urlParams.mailTagIds ? parseInt(urlParams.mailTagIds, 10) : undefined,
				customFolderId: urlParams.clientFolderIds ? parseInt(urlParams.clientFolderIds, 10) : undefined,
			};

			if (mailBoxInfo.folderSlug !== (urlParams.folderSlug || initialFolder)) {
				ResetSelection();
			}

			dispatchMailboxesStoreUpdate({
				type: 'updateMailboxInfo',
				payload: newData,
			});
		}

		dispatchMailboxesStoreUpdate({
			type: 'updateParentFolderId',
			folderId: folderParentFolderId,
		});
	};

	const MoveItemsToFolder = (targetFolderId: number) => {
		dispatchMailboxesStoreUpdate({
			type: 'setItemMoving',
			state: true,
		});

		addItemToCustomFolder(urlParams.mailId ? [urlParams.mailId] : selectedItems, targetFolderId)
			.then(() => {
				updateMailBox.mutate({
					newData: {
						mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
						folderName: urlParams.clientFolderIds ? 'custom-folder' : mailBoxInfo.folderSlug,
						targetFolderId,
					},
					updateType: MailItemUpdateType.MOVE,
					mailBoxUrlSlugs: parsedFilters,
				});
			})
			.finally(() => {
				ResetSelection();
				dispatchMailboxesStoreUpdate({
					type: 'setItemMoving',
					state: false,
				});
			});
	};

	const RemoveItemsFromFolder = (currentFolderId: number) => {
		dispatchMailboxesStoreUpdate({
			type: 'setItemMoving',
			state: true,
		});

		removeItemFromCustomFolder(urlParams.mailId ? [urlParams.mailId] : selectedItems, currentFolderId)
			.then(() => {
				updateMailBox.mutate({
					newData: {
						mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
						folderName: urlParams.clientFolderIds ? 'custom-folder' : mailBoxInfo.folderSlug,
						targetFolderId: currentFolderId,
					},
					updateType: MailItemUpdateType.REMOVE,
					mailBoxUrlSlugs: parsedFilters,
				});
			})
			.finally(() => {
				ResetSelection();

				dispatchMailboxesStoreUpdate({
					type: 'setItemMoving',
					state: false,
				});
			});
	};

	const MoveSelectedItems = useCallback(
		(targetFolderId: number) => {
			if (selectedItems.length > 0) {
				MoveItemsToFolder(targetFolderId);
				ResetSelection();
			}
		},
		[selectedItems],
	);

	const UpdateMailTags = (tag: any, checked: boolean) => {
		updateMailBox.mutate({
			newData: {
				mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
				tags: { ...tag },
				isChecked: checked,
				folderName: urlParams.clientFolderIds ? 'custom-folder' : mailBoxInfo.folderSlug,
			},
			updateType: MailItemUpdateType.TAGS,
			mailBoxUrlSlugs: parsedFilters,
		});
	};

	const UpdateMailNote = (noteValue: string, oldNoteValue: string) => {
		if (noteValue !== oldNoteValue) {
			dispatchMailboxesStoreUpdate({
				type: 'updateMailNoteData',
				payload: { data: null, success: false, updating: true, error: false },
			});

			updateMailNote(urlParams.mailId as string, noteValue)
				.then((r) => {
					updateMailBox.mutate({
						newData: {
							mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
							noteContent: noteValue,
						},
						updateType: MailItemUpdateType.NOTE,
						mailBoxUrlSlugs: parsedFilters,
					});

					dispatchMailboxesStoreUpdate({
						type: 'updateMailNoteData',
						payload: { data: r, success: true, updating: false, error: false },
					});
				})
				.catch((ex) => {
					dispatchMailboxesStoreUpdate({
						type: 'updateMailNoteData',
						payload: { data: ex, success: false, updating: false, error: true },
					});
				});
		}
	};

	const MarkMailItemAsRead = () => {
		updateMarkMailItemAsRead(urlParams.mailId as string).then(() => {
			updateMailBox.mutate({
				newData: {
					mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
					dateRead: new Date(),
				},
				updateType: MailItemUpdateType.MARK_AS_READ,
				mailBoxUrlSlugs: parsedFilters,
			});
		});
	};

	const ActionMailItemsUi = (actionName: string) => {
		updateMailBox.mutate({
			newData: {
				mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
				folderName: urlParams.clientFolderIds ? 'custom-folder' : mailBoxInfo.folderSlug,
				newStatus: {
					key: 'test_key',
					description: actionName,
					status: 'pending',
					requiresUserInput: false,
				},
			},
			updateType: MailItemUpdateType.MAIL_ACTION,
			mailBoxUrlSlugs: parsedFilters,
		});

		updateMailBox.mutate({
			newData: {
				mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
				folderName: urlParams.clientFolderIds ? 'custom-folder' : mailBoxInfo.folderSlug,
			},
			updateType: MailItemUpdateType.REMOVE,
			mailBoxUrlSlugs: parsedFilters,
		});
	};

	const UndoActionMailItems = () => {
		updateMailBox.mutate({
			newData: {
				mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
				folderName: urlParams.clientFolderIds ? 'custom-folder' : mailBoxInfo.folderSlug,
				newStatus: preItemUpdate?.mailAction,
				newActions: preItemUpdate?.actions,
			},
			updateType: MailItemUpdateType.UNDO_MAIL_ACTION,
			mailBoxUrlSlugs: parsedFilters,
		});

		updateMailBox.mutate({
			newData: {
				mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
				folderName: urlParams.clientFolderIds ? 'custom-folder' : mailBoxInfo.folderSlug,
			},
			updateType: MailItemUpdateType.RESTORE,
			mailBoxUrlSlugs: parsedFilters,
		});
	};

	const ActionMailItems = (actionId: number, actionName: string, successCallback?: any) => {
		ActionMailItemsUi(actionName);
		ResetSelection();
		actionMailItems(urlParams.mailId ? [urlParams.mailId] : selectedItems, actionId)
			.then(() => {
				// ActionMailItemsUi();
			})
			.finally(() => {
				if (successCallback) {
					successCallback();
				}
			});
	};

	const SetGridView = (value: boolean) => {
		localStorage.setItem(`${process.env.REACT_APP_SITE_KEY}-mailbox-view-type`, value ? 'grid' : 'list');
		setGridView(value);
	};

	const SetGridSize = (size: number) => {
		const newValue = { size, className: MailBoxGridSize[size] };
		localStorage.setItem(`${process.env.REACT_APP_SITE_KEY}-mailbox-grid-size`, JSON.stringify(newValue));
		setGridSize(newValue);
	};

	const ToggleCreateCustomFolderModal = (state: boolean) => {
		dispatchMailboxesStoreUpdate({
			type: 'toggleCustomFolderModal',
			state,
		});

		if (!state && activeCustomFolderData) {
			dispatchMailboxesStoreUpdate({
				type: 'updateCustomFolderData',
				payload: null,
			});
		}
	};

	const ToggleDeleteCustomFolderModal = (state: boolean) => {
		dispatchMailboxesStoreUpdate({
			type: 'toggleDeleteCustomFolderModal',
			state,
		});

		if (!state && activeCustomFolderData) {
			dispatchMailboxesStoreUpdate({
				type: 'updateCustomFolderData',
				payload: null,
			});
		}
	};

	const UpdateActiveFolderData = (folderId: number) => {
		if (!customFolderData) return;
		const folderIndex = GetArrayIndexByKeyValue(customFolderData, 'id', folderId);
		const parentFolderIndex = GetArrayIndexByKeyValue(customFolderData, 'parentFolderId', folderId);

		dispatchMailboxesStoreUpdate({
			type: 'updateCustomFolderData',
			payload: { ...customFolderData[folderIndex], childFolders: parentFolderIndex > -1 },
		});
	};

	const CreateCustomFolder = (
		newFolderData: CustomMailFolderType,
		SuccessCallback?: (newFolderId: number) => void,
	) => {
		const newFolder: CustomMailFolderType = {
			id: Math.floor(Math.random() * (1000 - 1 + 1) + 1),
			description: newFolderData.description,
			colour: newFolderData.colour,
			parentFolderId: newFolderData.parentFolderId !== -1 ? newFolderData.parentFolderId : null,
		};

		createCustomFolder(newFolderData).then((resp) => {
			updateCustomFolder.mutate({
				newData: {
					folderData: {
						...newFolder,
						id: resp.id,
					},
				},
				updateType: MailFolderUpdateType.ADD,
			});

			ToggleCreateCustomFolderModal(false);

			if (selectedItems.length > 0) {
				MoveItemsToFolder(resp.id);
			}

			if (SuccessCallback) {
				SuccessCallback(resp.id);
			}
		});
	};

	const EditCustomFolder = (editFolderData: CustomMailFolderType, SuccessCallback?: () => void) => {
		if (!activeCustomFolderData) return;

		dispatchMailboxesStoreUpdate({
			type: 'setCustomFolderUpdating',
			state: true,
		});

		const newFolderData: any = {};

		newFolderData.id = activeCustomFolderData.id;
		newFolderData.description = editFolderData.description;
		newFolderData.colour = editFolderData.colour;
		newFolderData.parentFolderId = editFolderData.parentFolderId !== -1 ? editFolderData.parentFolderId : null;

		editCustomFolder(newFolderData)
			.then((resp) => {
				updateCustomFolder.mutate({
					newData: {
						targetFolderId: activeCustomFolderData.id,
						folderData: newFolderData,
					},
					updateType: MailFolderUpdateType.EDIT,
				});

				ToggleCreateCustomFolderModal(false);

				if (SuccessCallback) {
					SuccessCallback();
				}
			})
			.finally(() => {
				dispatchMailboxesStoreUpdate({
					type: 'setCustomFolderUpdating',
					state: false,
				});
			});
	};

	const DeleteCustomFolder = (deleteChildFolders = false) => {
		if (!activeCustomFolderData) return;

		dispatchMailboxesStoreUpdate({
			type: 'setCustomFolderUpdating',
			state: true,
		});

		deleteCustomFolder(activeCustomFolderData?.id, deleteChildFolders)
			.then(() => {
				updateCustomFolder.mutate({
					newData: {
						targetFolderId: activeCustomFolderData.id,
						deleteChildFolders,
					},
					updateType: MailFolderUpdateType.REMOVE,
				});

				ToggleDeleteCustomFolderModal(false);
			})
			.finally(() => {
				dispatchMailboxesStoreUpdate({
					type: 'setCustomFolderUpdating',
					state: false,
				});
			});
	};

	const MoveCustomFolder = (targetFolderId: number | null, currentFolderId: number) => {
		if (!customFolderData) return;

		dispatchMailboxesStoreUpdate({
			type: 'setCustomFolderUpdating',
			state: true,
		});

		const folderIndex = GetArrayIndexByKeyValue(customFolderData, 'id', currentFolderId);
		const newData = { ...customFolderData[folderIndex] };
		newData.parentFolderId = targetFolderId;

		editCustomFolder(newData)
			.then((r) => {
				updateCustomFolder.mutate({
					newData: {
						targetFolderId,
						currentFolderId,
					},
					updateType: MailFolderUpdateType.CHANGE_PARENT,
				});
			})
			.finally(() => {
				dispatchMailboxesStoreUpdate({
					type: 'setCustomFolderUpdating',
					state: false,
				});
			});
	};

	const GetMailItemsByUuid = (data: InfiniteData<MailBoxResponseType>, Uuids: Array<string>): Array<MailItemType> => {
		const collection: Array<MailItemType> = [];

		data.pages.forEach((page) => {
			page.data.forEach((item) => (Uuids.includes(item.mailUuid) ? collection.push(item) : null));
		});

		return collection;
	};

	const ForwardItemsDataCleanse = () => {
		dispatchMailboxesStoreUpdate({
			type: 'updateForwardItemsData',
			payload: { open: false, mailIds: [], mailItems: [] },
		});
	};

	const ForwardItemsComplete = () => {
		ActionMailItemsUi('Forward');

		ForwardItemsDataCleanse();

		ResetSelection();
	};

	const CreateForwardingBatch = (data: CreateForwardingBatchType) => {
		createForwardingBatch(data as CreateForwardingBatchType)
			.then((r) => {
				navigate(`/mail/forwarding-batches/${r.batchId}`);
				ForwardItemsComplete();
			})
			.catch((e) => console.log('create error', e));
	};

	const ActionItem = (data: InfiniteData<MailBoxResponseType> | MailItemType, actionId: number) => {
		if (actionId === 0) {
			const mailItems =
				'pages' in data
					? GetMailItemsByUuid(data, urlParams.mailId ? [urlParams.mailId] : selectedItems)
					: [data];

			dispatchMailboxesStoreUpdate({
				type: 'updateForwardItemsData',
				payload: {
					open: true,
					mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
					mailItems,
				},
			});
		} else {
			dispatchMailboxesStoreUpdate({
				type: 'updateActionConfirmationData',
				payload: {
					open: true,
					actionId,
					mailIds: urlParams.mailId ? [urlParams.mailId] : selectedItems,
				},
			});
		}
	};

	const ActionItemsDataCleanse = () => {
		dispatchMailboxesStoreUpdate({
			type: 'updateActionConfirmationData',
			payload: { open: false, actionId: -1, mailIds: [] },
		});
	};

	const ActionItemComplete = (actionId: number) => {
		ResetSelection();
		ActionItemsDataCleanse();

		if (actionId === 6 || actionId === 7) {
			navigate('/mail/inbox');
		}
	};

	const HandleFolderMove = (e: any) => {
		const newResult = e.currentId ? e : DropCustomFolder(e);

		if (newResult.targetId && newResult.currentId) {
			MoveCustomFolder(newResult.targetId, newResult.currentId);
		}
	};

	const ShowFilters = () => {
		dispatchMailboxesStoreUpdate({
			type: 'toggleMailboxesFilterModal',
			state: true,
		});
	};

	const SetFilters = (filters: FilterableMailBoxUrlSlugs) => {
		const newFilters = UpdateAllFilters(filters);
		setUserFilters(filters);
		setParsedFilters({ ...privateFilters, ...newFilters });
	};

	const RemoveFilter = (filterName: keyof FilterableMailBoxUrlSlugs) => {
		const newFilters = DeleteFilter(userFilters, filterName);
		setUserFilters(newFilters);
		setParsedFilters({ ...privateFilters, ...newFilters });
	};

	const ClearFilters = () => {
		setUserFilters({});
		setParsedFilters({ ...privateFilters });
	};

	const UpdateUrlSearchParams = (newParams: URLSearchParams) => {
		setUrlSearchParams(newParams);
	};

	const RemoveUrlSearchParam = (key: string) => {
		urlSearchParams.delete(key);
		UpdateUrlSearchParams(urlSearchParams);
	};

	const UpdateAllFiltersAndParams = () => {
		const newFilterable = MailboxFilterUrlMapping(params, location);
		const newPrivate = MailboxUrlMapping(params, location);

		ResetSelection();
		setUrlParams(params);
		setUserFilters(newFilterable);
		setPrivateFilters(newPrivate);
		setParsedFilters({ ...newPrivate, ...newFilterable });
	};

	useEffect(() => {
		if (urlParams.mailId && mailItemData) {
			dispatchMailboxesStoreUpdate({
				type: 'updateActiveMailItem',
				preItemUpdate: mailItemData,
				mailItemOnHold: mailItemData?.holdReason !== null,
				mailItemRequiresTopUp:
					mailItemData?.files && mailItemData?.files.length > 0 && mailItemData?.files[0].topupRequired,
			});

			FilterActions([mailItemData], [mailItemData?.mailUuid]);
		}
	}, [allSelected, selectedItems, urlParams.mailId, mailItemData]);

	useEffect(() => {
		if (
			(urlParams.mailTagIds ||
				urlParams.mailId ||
				urlParams.clientFolderIds ||
				urlParams.folderSlug ||
				urlParams.unreadStatus ||
				urlParams.mailType) &&
			customFolderStatus !== 'pending'
		) {
			UpdateMailboxInfo();

			dispatchMailboxesStoreUpdate({
				type: 'updateMailNoteData',
				payload: { data: null, success: false, updating: false, error: false },
			});
		}
	}, [urlParams, customFolderData]);

	useEffect(() => {
		UpdateAllFiltersAndParams();
	}, [params, location]);

	useEffect(() => {
		selectedItemsRef.current = selectedItems;
	}, [selectedItems]);

	useEffect(() => {
		if (location.pathname.indexOf('/mail/search') === -1) {
			setLastNonSearchLocation(location.pathname);
		}
		setSearchActive(location.pathname.indexOf('/mail/search') > -1);
	}, [params, location]);

	useLayoutEffect(() => {
		setSearchParams(urlSearchParams);
	}, [urlSearchParams]);

	const mailboxFilterMemo = useMemo(
		() => ({
			mailBoxInfo,

			folderStatus,
			folderData,
			folderError,

			customFolderStatus,
			customFolderData,
			customFolderError,

			mailItemStatus,
			mailItemData,
			mailItemError,

			userFilters,
			privateFilters,
			parsedFilters,

			UpdateMailTags,
			MarkMailItemAsRead,
			UpdateMailNote,
			MoveSelectedItems,
			MoveItemsToFolder,
			RemoveItemsFromFolder,

			ActionMailItemsUi,
			UndoActionMailItems,

			ActionMailItems,

			ActionItem,
			ActionItemComplete,

			ShowFilters,
			SetFilters,
			RemoveFilter,
			ClearFilters,

			lastNonSearchLocation,
			searchActive,
			searchReset,
			searchParams,
			UpdateUrlSearchParams,
			RemoveUrlSearchParam,

			itemMoving,

			allSelected,
			visibleSelected,
			visibleIndeterminate,
			selectedItems,
			selectedItemsRef,

			ToggleAll,
			ToggleSelected,
			ToggleVisible,
			ResetSelection,

			gridSize,
			gridView,
			SetGridView,
			SetGridSize,

			FilterActions,
			selectionActions,
			mailItemActionsError,
			mailItemActionsStatus,

			createCustomFolderOpen,
			deleteCustomFolderOpen,
			activeCustomFolderData,

			ToggleCreateCustomFolderModal,
			ToggleDeleteCustomFolderModal,
			UpdateActiveFolderData,
			CreateCustomFolder,
			EditCustomFolder,
			DeleteCustomFolder,
			MoveCustomFolder,
			mailFolderModificationActive,

			HandleFolderMove,

			CreateForwardingBatch,

			mailNote,

			mailItemRequiresTopUp,
			mailItemOnHold,
		}),
		[
			mailFolderModificationActive,
			itemMoving,
			gridSize,
			gridView,
			allSelected,
			visibleSelected,
			visibleIndeterminate,
			selectedItems,
			selectedItemsRef,
			mailBoxInfo,
			folderStatus,
			folderData,
			folderError,
			customFolderStatus,
			customFolderData,
			customFolderError,

			userFilters,
			privateFilters,
			parsedFilters,

			selectionActions,
			mailItemActionsError,
			mailItemActionsStatus,

			mailItemStatus,
			mailItemData,
			mailItemError,

			createCustomFolderOpen,
			deleteCustomFolderOpen,
			activeCustomFolderData,

			mailNote,

			mailItemRequiresTopUp,
			mailItemOnHold,

			lastNonSearchLocation,
			searchActive,
			searchReset,
			searchParams,
		],
	);

	return (
		<MailboxContext.Provider value={mailboxFilterMemo}>
			{props.children}

			<FilterMailboxModal
				isOpen={showFilterModal}
				closeCallback={() =>
					dispatchMailboxesStoreUpdate({
						type: 'toggleMailboxesFilterModal',
						state: false,
					})
				}
				searchParams={searchParams}
				searchReset={searchReset}
				UpdateUrlSearchParams={(newParams) => UpdateUrlSearchParams(newParams)}
			/>

			<CreateCustomFolderModal
				isOpen={createCustomFolderOpen}
				closeCallback={() => ToggleCreateCustomFolderModal(false)}
				customFolderStatus={customFolderStatus}
				customFolderError={customFolderError}
				activeCustomFolderData={activeCustomFolderData}
				CreateCustomFolderCallback={(newFolderData, SuccessCallback) =>
					CreateCustomFolder(newFolderData, SuccessCallback)
				}
				EditCustomFolderCallback={(editFolderData, SuccessCallback) =>
					EditCustomFolder(editFolderData, SuccessCallback)
				}
			/>

			<DeleteCustomFolderModal
				isOpen={deleteCustomFolderOpen}
				closeCallback={() => ToggleDeleteCustomFolderModal(false)}
				deleteCustomFolderOpen={deleteCustomFolderOpen}
				activeCustomFolderData={activeCustomFolderData}
				DeleteCustomFolderCallback={(state) => DeleteCustomFolder(state)}
				ToggleDeleteCustomFolderModalCallback={(state) => ToggleDeleteCustomFolderModal(state)}
			/>

			<ForwardItemsModal
				isOpen={forwardItemsData.open}
				mailIds={forwardItemsData.mailIds}
				mailItems={forwardItemsData.mailItems}
				closeCallback={() => ForwardItemsDataCleanse()}
				actionedCallback={() => ForwardItemsComplete()}
			/>

			<ConfirmActionModal
				isOpen={confirmActionData.open}
				folderSlug={mailBoxInfo.folderSlug}
				mailIds={confirmActionData.mailIds}
				actionId={confirmActionData.actionId}
				closeCallback={() => ActionItemsDataCleanse()}
				actionedCallback={() => ActionItemComplete(confirmActionData.actionId)}
				actionMailItemsCallback={(id, name, callback) => ActionMailItems(id, name, callback)}
				actionMailItemsUiCallback={(name) => ActionMailItemsUi(name)}
				undoActionMailItemsCallback={() => UndoActionMailItems()}
			/>
		</MailboxContext.Provider>
	);
}

const useMailbox = () => useContext(MailboxContext);

export { MailboxContext, useMailbox };

export default MailboxProvider;
